import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";

import {
  cancelOptimizeJob,
  getOptimizeJob,
  postOptimizeJob,
  type OptimizeJobStatus,
  type OptimizeRequest,
  type OptimizeResult,
} from "@/api/optimize-allocation";
import { getApiErrorMessage } from "@penn/web-common";
import {
  isRequestAbortError,
  toCalculationRateLimitError,
} from "@/utils/calculation-request-control";

// =============================================================================
// useOptimizeAllocation — drives the Q-OPT-03 D job lifecycle from the FE:
// enqueue → poll → terminal. Exposes progress (processed/total) for the modal
// loader and a cancel handle. Phase-1 jobs are in-memory on the server, so a
// page refresh loses the job (documented in the deferred jobs spec).
// =============================================================================

const POLL_INTERVAL_MS = 800;
const POLL_ERROR_BASE_BACKOFF_MS = 1_000;
const POLL_ERROR_MAX_BACKOFF_MS = 8_000;
const CANCEL_ERROR_BASE_BACKOFF_MS = 500;
const CANCEL_ERROR_MAX_BACKOFF_MS = 2_000;
const CANCEL_ERROR_MAX_ATTEMPTS = 4;

type OptimizeRunContext = {
  cancelRequested: boolean;
  jobId: string | null;
  cancelPromise: Promise<void> | null;
  pollFailureCount: number;
};

function isRetryablePollFailure(error: unknown): boolean {
  if (!isAxiosError(error)) return true;
  const status = error.response?.status;
  return status === undefined || status === 408 || status === 425 || status >= 500;
}

export type OptimizeState =
  | { status: "idle" }
  | {
      status: "running";
      phase: OptimizeJobStatus;
      processed: number;
      total: number;
      /** Present while a poll is paused until the server's Retry-After deadline. */
      retryMessage?: string;
      retryAutomatically?: boolean;
    }
  | { status: "completed"; result: OptimizeResult }
  | { status: "cancelled" }
  | { status: "error"; message: string };

export function useOptimizeAllocation() {
  const [state, setState] = useState<OptimizeState>({ status: "idle" });
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollControllerRef = useRef<AbortController | null>(null);
  const activeRunRef = useRef<OptimizeRunContext | null>(null);
  const mountedRef = useRef(true);
  const rateLimitUntilRef = useRef(0);
  const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const recordRateLimit = useCallback(
    (retryAtMs: number) => {
      rateLimitUntilRef.current = retryAtMs;
      if (mountedRef.current) setIsRateLimited(true);
      if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
      rateLimitTimerRef.current = setTimeout(() => {
        rateLimitUntilRef.current = 0;
        rateLimitTimerRef.current = null;
        if (mountedRef.current) setIsRateLimited(false);
      }, Math.max(0, retryAtMs - Date.now()));
    },
    [],
  );

  const cancelServerJob = useCallback(
    async (jobId: string) => {
      let cancelNotBeforeMs = rateLimitUntilRef.current;
      let transientFailureCount = 0;
      // A DELETE sent inside the same gateway rate-limit window would itself
      // receive 429. Wait out that window and retry any cancellation 429 until
      // the gateway accepts the request. Network/5xx failures get a bounded
      // backoff too: silently giving up after one transient failure can leave a
      // server job running after the UI has reported it cancelled.
      for (;;) {
        const waitMs = Math.max(0, cancelNotBeforeMs - Date.now());
        if (waitMs > 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
        }
        try {
          await cancelOptimizeJob(jobId);
          return;
        } catch (error) {
          const rateLimitError = toCalculationRateLimitError(error);
          if (rateLimitError) {
            cancelNotBeforeMs = rateLimitError.retryAtMs;
            recordRateLimit(cancelNotBeforeMs);
            continue;
          }
          transientFailureCount += 1;
          if (
            !isRetryablePollFailure(error) ||
            transientFailureCount >= CANCEL_ERROR_MAX_ATTEMPTS
          ) {
            return;
          }
          const retryInMs = Math.min(
            CANCEL_ERROR_MAX_BACKOFF_MS,
            CANCEL_ERROR_BASE_BACKOFF_MS *
              2 ** Math.min(transientFailureCount - 1, 2),
          );
          cancelNotBeforeMs = Date.now() + retryInMs;
        }
      }
    },
    [recordRateLimit],
  );

  const cancelRunContext = useCallback(
    async (context: OptimizeRunContext) => {
      context.cancelRequested = true;
      if (!context.jobId) return;
      if (!context.cancelPromise) {
        context.cancelPromise = cancelServerJob(context.jobId).finally(() => {
          if (activeRunRef.current === context) activeRunRef.current = null;
        });
      }
      await context.cancelPromise;
    },
    [cancelServerJob],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPoll();
      pollControllerRef.current?.abort();
      pollControllerRef.current = null;
      if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
      const context = activeRunRef.current;
      if (context) void cancelRunContext(context);
    };
  }, [cancelRunContext, clearPoll]);

  const reset = useCallback(() => {
    clearPoll();
    pollControllerRef.current?.abort();
    pollControllerRef.current = null;
    // Do not clear/revalidate activeRunRef: Cancel may be waiting for a slow
    // enqueue response so it can DELETE the server-created job immediately.
    if (mountedRef.current) setState({ status: "idle" });
  }, [clearPoll]);

  const beginPreparing = useCallback(() => {
    clearPoll();
    pollControllerRef.current?.abort();
    pollControllerRef.current = null;
    const previous = activeRunRef.current;
    if (previous) void cancelRunContext(previous);
    if (mountedRef.current) {
      setState({ status: "running", phase: "queued", processed: 0, total: 0 });
    }
  }, [cancelRunContext, clearPoll]);

  const fail = useCallback((message: string) => {
    if (mountedRef.current) setState({ status: "error", message });
  }, []);

  const poll = useCallback(
    async (context: OptimizeRunContext) => {
      const jobId = context.jobId;
      if (
        !jobId ||
        context.cancelRequested ||
        activeRunRef.current !== context
      ) {
        return;
      }
      const controller = new AbortController();
      pollControllerRef.current?.abort();
      pollControllerRef.current = controller;
      try {
        const res = await getOptimizeJob(jobId, controller.signal);
        if (
          context.cancelRequested ||
          activeRunRef.current !== context ||
          !mountedRef.current
        ) {
          return;
        }
        context.pollFailureCount = 0;

        if (res.status === "completed" && res.result) {
          activeRunRef.current = null;
          setState({ status: "completed", result: res.result });
          return;
        }
        if (res.status === "failed") {
          activeRunRef.current = null;
          setState({
            status: "error",
            message: res.error?.message ?? "Optimization failed.",
          });
          return;
        }
        if (res.status === "cancelled") {
          activeRunRef.current = null;
          setState({ status: "cancelled" });
          return;
        }
        setState({
          status: "running",
          phase: res.status,
          processed: res.processedCombinations,
          total: res.totalCombinations,
        });
        pollRef.current = setTimeout(
          () => void poll(context),
          POLL_INTERVAL_MS,
        );
      } catch (e) {
        if (
          context.cancelRequested ||
          activeRunRef.current !== context ||
          isRequestAbortError(e)
        )
          return;
        const rateLimitError = toCalculationRateLimitError(e);
        if (rateLimitError) {
          recordRateLimit(rateLimitError.retryAtMs);
          // A polling 429 does not terminate the server-side job. Keep its
          // identity/progress and the modal's Cancel action alive, then resume
          // polling exactly when Retry-After permits another request.
          setState((previous) =>
            previous.status === "running"
              ? {
                  ...previous,
                  retryMessage: rateLimitError.message,
                  retryAutomatically: true,
                }
              : {
                  status: "running",
                  phase: "running",
                  processed: 0,
                  total: 0,
                  retryMessage: rateLimitError.message,
                  retryAutomatically: true,
                },
          );
          pollRef.current = setTimeout(
            () => void poll(context),
            Math.max(0, rateLimitError.retryAtMs - Date.now()),
          );
          return;
        }
        if (!isRetryablePollFailure(e)) {
          const detail = getApiErrorMessage(
            e,
            "Could not check optimization progress.",
          );
          setState((previous) =>
            previous.status === "running"
              ? {
                  ...previous,
                  retryMessage: `${detail} Automatic progress checks stopped. Cancel optimization to close this window safely.`,
                  retryAutomatically: false,
                }
              : previous,
          );
          return;
        }
        context.pollFailureCount += 1;
        const retryInMs = Math.min(
          POLL_ERROR_MAX_BACKOFF_MS,
          POLL_ERROR_BASE_BACKOFF_MS *
            2 ** Math.min(context.pollFailureCount - 1, 3),
        );
        const seconds = Math.max(1, Math.ceil(retryInMs / 1_000));
        const detail = getApiErrorMessage(e, "Could not check optimization progress.");
        setState((previous) =>
          previous.status === "running"
            ? {
                ...previous,
                retryMessage: `${detail} Retrying automatically in ${seconds} ${seconds === 1 ? "second" : "seconds"}.`,
                retryAutomatically: true,
              }
            : previous,
        );
        pollRef.current = setTimeout(
          () => void poll(context),
          retryInMs,
        );
      } finally {
        if (pollControllerRef.current === controller) {
          pollControllerRef.current = null;
        }
      }
    },
    [recordRateLimit],
  );

  const run = useCallback(
    async (body: OptimizeRequest) => {
      if (Date.now() < rateLimitUntilRef.current) {
        const seconds = Math.max(
          1,
          Math.ceil((rateLimitUntilRef.current - Date.now()) / 1_000),
        );
        setState({
          status: "error",
          message: `Too many calculation requests. Please retry in ${seconds} ${seconds === 1 ? "second" : "seconds"}.`,
        });
        return;
      }
      clearPoll();
      pollControllerRef.current?.abort();
      pollControllerRef.current = null;
      const previous = activeRunRef.current;
      if (previous) void cancelRunContext(previous);
      const context: OptimizeRunContext = {
        cancelRequested: false,
        jobId: null,
        cancelPromise: null,
        pollFailureCount: 0,
      };
      activeRunRef.current = context;
      setState({ status: "running", phase: "queued", processed: 0, total: 0 });
      // Once an enqueue POST is sent it must not be aborted: the server may
      // create the job before the browser receives its ID. A per-run context
      // records Cancel/unmount while this response is pending and cleans up the
      // returned job immediately.
      const enqueueController = new AbortController();
      try {
        const enq = await postOptimizeJob(body, enqueueController.signal);
        context.jobId = enq.jobId;
        if (
          context.cancelRequested ||
          activeRunRef.current !== context ||
          !mountedRef.current
        ) {
          await cancelRunContext(context);
          return;
        }
        setState({
          status: "running",
          phase: enq.status,
          processed: enq.processedCombinations ?? 0,
          total: enq.totalCombinations,
        });
        void poll(context);
      } catch (e) {
        if (
          context.cancelRequested ||
          activeRunRef.current !== context ||
          !mountedRef.current
        ) {
          return;
        }
        const rateLimitError = toCalculationRateLimitError(e);
        if (rateLimitError) recordRateLimit(rateLimitError.retryAtMs);
        activeRunRef.current = null;
        setState({
          status: "error",
          message:
            rateLimitError?.message ??
            getApiErrorMessage(e, "Failed to start optimization."),
        });
      }
    },
    [cancelRunContext, clearPoll, poll, recordRateLimit],
  );

  const cancel = useCallback(async () => {
    clearPoll();
    pollControllerRef.current?.abort();
    pollControllerRef.current = null;
    const context = activeRunRef.current;
    if (context) context.cancelRequested = true;
    if (mountedRef.current) setState({ status: "cancelled" });
    if (context) await cancelRunContext(context);
  }, [cancelRunContext, clearPoll]);

  return {
    state,
    run,
    cancel,
    reset,
    beginPreparing,
    fail,
    isRateLimited,
    isRunning: state.status === "running",
  };
}
